import { Button, IconButton, Typography } from "@mui/material";
import { CenterPivotLeg, DeepPartial, Zone } from "protos/farm/farm";
import { FieldArray, Formik } from "formik";
import { FormikDimensionedNumberInput } from "common/components/FormikDimensionedNumberInput";
import { FormikTextInput } from "common/components/FormikTextInput";
import { Point } from "./Point";
import { Section } from "portal/components/map/navigation/Section";
import { setSelection } from "portal/state/farmExplorer";
import { useDispatch } from "react-redux";
import { useFarmExplorer, useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import React, { FC, useMemo } from "react";
import SaveIcon from "@mui/icons-material/Save";

interface CenterPivotProps {
  zone: Zone;
  updateZone: (newZone: DeepPartial<Zone>) => void;
}

export const CenterPivot: FC<CenterPivotProps> = ({
  zone,
  updateZone,
}: CenterPivotProps) => {
  const { t } = useTranslation();
  const { measurementSystem } = useSelf();
  const { pointsById } = useFarmExplorer();
  const { zonesById } = useFarmExplorer();
  const dispatch = useDispatch();
  const { linkedFields } = useMemo(() => {
    return {
      linkedFields: zonesById
        ? Object.values(zonesById).filter(
            (z) =>
              z.contents?.field &&
              z.contents.field.centerPivotIds.some((i) => i.id === zone.id?.id)
          )
        : [],
    };
  }, [zonesById, zone]);
  const pivot = zone.contents?.centerPivot;
  if (!pivot) {
    return;
  }
  const centerPoint = pivot.center?.id?.id
    ? pointsById?.[pivot.center.id.id]
    : undefined;

  const BLANK_LEG: CenterPivotLeg = {
    widthMeters: 1,
    lengthMeters: 0,
    endpointDeviceId: "",
  };

  return (
    <Formik
      initialValues={zone}
      enableReinitialize
      onSubmit={(values) => {
        updateZone({
          ...values,
          version: { changed: true, ordinal: values.version?.ordinal },
        });
      }}
    >
      {(formikProps) => {
        return (
          <div>
            <Point point={centerPoint} />
            <Section header={t("models.farms.centerPivot.leg_other")}>
              <FieldArray
                name="contents.centerPivot.legs"
                render={(arrayHelpers) => (
                  <div className="flex flex-col p-2">
                    <div className="max-h-96 overflow-y-auto flex flex-col gap-2">
                      {formikProps.values.contents?.centerPivot?.legs.map(
                        (_l, i) => (
                          <div
                            key={`${i}-legs`}
                            className="p-2 flex flex-col bg-darken-200 gap-1"
                          >
                            <div className="flex justify-between">
                              <Typography className="flex my-auto">
                                {i + 1}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => arrayHelpers.remove(i)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </div>
                            <FormikTextInput
                              className="w-full"
                              name={`contents.centerPivot.legs[${i}].endpointDeviceId`}
                              label={t("views.farms.detailsPanel.endpointId")}
                            />
                            <FormikDimensionedNumberInput
                              className="w-full"
                              label={t("views.farms.detailsPanel.length")}
                              name={`contents.centerPivot.legs[${i}].lengthMeters`}
                              dimensionedNumberInputProps={{
                                storedUnit: "m",
                                displayUnits: {
                                  imperial: "ft",
                                  metric: "m",
                                },
                                localSystem: measurementSystem,
                                roundDisplayToNPlaces: 1,
                              }}
                            />
                            <FormikDimensionedNumberInput
                              className="w-full"
                              label={t("views.farms.detailsPanel.width")}
                              name={`contents.centerPivot.legs[${i}].widthMeters`}
                              dimensionedNumberInputProps={{
                                storedUnit: "m",
                                displayUnits: {
                                  imperial: "ft",
                                  metric: "m",
                                },
                                localSystem: measurementSystem,
                                roundDisplayToNPlaces: 1,
                              }}
                            />
                          </div>
                        )
                      )}
                      <Button
                        className="m-1"
                        fullWidth
                        color="secondary"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => arrayHelpers.push(BLANK_LEG)}
                      >
                        {t("utils.actions.addLong", {
                          subject: t("models.farms.centerPivot.leg_one"),
                        })}
                      </Button>
                    </div>
                    <div className="flex w-full m-1 gap-2">
                      <Button
                        fullWidth
                        startIcon={<SaveIcon />}
                        onClick={formikProps.submitForm}
                        disabled={!formikProps.dirty}
                        variant="contained"
                      >
                        {t("utils.actions.saveLong", {
                          subject: t("models.farms.centerPivot.leg_other"),
                        })}
                      </Button>
                      {formikProps.dirty && (
                        <Button
                          fullWidth
                          variant="contained"
                          color="inherit"
                          onClick={() => formikProps.resetForm()}
                          disabled={!formikProps.dirty}
                        >
                          {t("utils.actions.discard")}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              />
            </Section>
            <Section header={t("views.farms.detailsPanel.linkedFields")}>
              <div className="flex flex-col p-2">
                {linkedFields.map((f) => (
                  <Button
                    color="inherit"
                    key={f.id?.id}
                    onClick={() =>
                      dispatch(setSelection({ type: "zone", selection: f }))
                    }
                  >
                    {f.name}
                  </Button>
                ))}
              </div>
            </Section>
          </div>
        );
      }}
    </Formik>
  );
};
